/* =========================================================
   GLOBAL — global.js
   Runs on every page: the real Shopify AJAX cart ("chest"),
   the mobile hamburger + search toggle, past-show graying,
   language filter tabs (Ledger + Catalogue), and the region
   card shortcuts on the homepage.
   ========================================================= */

/* ---------- Money formatting ----------
   Shopify's real money_format setting (e.g. "${{amount}}" or
   "{{amount_no_decimals}} kr") is exposed via window.themeMoneyFormat,
   set in layout/theme.liquid. This mirrors Shopify's public
   formatMoney helper closely enough for cart/drawer re-renders. */
function formatMoney(cents) {
  const format = window.themeMoneyFormat || "${{amount}}";
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.round(cents / 100).toString();
  return format
    .replace(/\{\{\s*amount\s*\}\}/, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals);
}

/* ---------- AJAX cart ---------- */
async function addToCart(variantId, quantity = 1) {
  if (!variantId) return;
  try {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      alert(err?.description || "That lot couldn't be added — it may be sold out.");
      return;
    }
    await refreshChest();
    openChestDrawer();
  } catch (e) {
    console.error("addToCart failed", e);
  }
}

async function changeCartLine(line, quantity) {
  try {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line, quantity })
    });
    const cart = await res.json();
    renderChestUI(cart);
    return cart;
  } catch (e) {
    console.error("changeCartLine failed", e);
  }
}

async function refreshChest() {
  const res = await fetch("/cart.js");
  const cart = await res.json();
  renderChestUI(cart);
  return cart;
}

function renderChestUI(cart) {
  const countEls = document.querySelectorAll("#chest-count");
  countEls.forEach(el => (el.textContent = cart.item_count));

  const itemsEl = document.getElementById("chest-items");
  if (itemsEl) {
    if (cart.item_count === 0) {
      itemsEl.innerHTML = `<div class="chest-empty">The chest is empty.<br>Claim a lot from the ledger to begin.</div>`;
    } else {
      itemsEl.innerHTML = cart.items.map((item, i) => `
        <div class="chest-line">
          <img src="${item.image}" alt="${item.title}">
          <div class="chest-line__info">
            <div class="chest-line__title">${item.product_title}</div>
            <div class="chest-line__meta">${item.variant_title || ""} &middot; Qty ${item.quantity}</div>
            <button class="chest-line__remove" data-remove data-line="${i + 1}">Release</button>
          </div>
          <div class="appraisal-price">${formatMoney(item.final_line_price)}</div>
        </div>
      `).join("");
    }
  }

  const subtotalEl = document.getElementById("chest-subtotal");
  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
}

function openChestDrawer() {
  document.getElementById("chest-drawer")?.classList.add("open");
  document.getElementById("overlay")?.classList.add("open");
}
function closeChestDrawer() {
  document.getElementById("chest-drawer")?.classList.remove("open");
  document.getElementById("overlay")?.classList.remove("open");
}

function wireChestDrawer() {
  // The cart icon is a real link to /cart (works with no JS); once JS is
  // live, open the drawer instead of navigating away.
  document.getElementById("chest-btn")?.addEventListener("click", (e) => {
    e.preventDefault();
    openChestDrawer();
  });
  document.getElementById("chest-close")?.addEventListener("click", closeChestDrawer);
  document.getElementById("overlay")?.addEventListener("click", closeChestDrawer);

  document.getElementById("chest-items")?.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-remove]");
    if (btn) changeCartLine(btn.dataset.line, 0);
  });
}

/* Delegated add-to-cart — covers ledger cards, catalogue cards,
   related-product cards and the spotlight/product-detail buttons,
   all in one listener instead of per-page wiring. */
function wireAddToCart() {
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-add]");
    if (!btn || btn.disabled) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    let qty = 1;
    if (btn.dataset.qtySource) {
      const qtyEl = document.getElementById(btn.dataset.qtySource);
      qty = Math.max(1, parseInt(qtyEl?.textContent, 10) || 1);
    }
    addToCart(variantId, qty);
  });
}

/* ---------- Mobile hamburger + search toggle ---------- */
function wireMobileNav() {
  const menuBtn = document.getElementById("mobile-menu-btn");
  const mobileNav = document.getElementById("mobile-nav");
  const searchBtn = document.getElementById("mobile-search-btn");
  const spyglass = document.getElementById("spyglass");

  menuBtn?.addEventListener("click", () => {
    mobileNav?.classList.toggle("open");
    if (mobileNav?.classList.contains("open")) {
      spyglass?.classList.remove("mobile-open");
    }
  });

  searchBtn?.addEventListener("click", () => {
    spyglass?.classList.toggle("mobile-open");
    if (spyglass?.classList.contains("mobile-open")) {
      mobileNav?.classList.remove("open");
      spyglass?.querySelector("input")?.focus();
    }
  });

  mobileNav?.addEventListener("click", (e) => {
    if (e.target.closest("a")) mobileNav.classList.remove("open");
  });
}

/* ---------- Trade show past-date graying ---------- */
function markPastShows() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  document.querySelectorAll(".event-card[data-date]").forEach(card => {
    const eventEnd = new Date(`${card.dataset.date}T23:59:59`);
    if (eventEnd < today) {
      card.classList.add("event-card--past");
      if (!card.querySelector(".event-stamp")) {
        const stamp = document.createElement("div");
        stamp.className = "event-stamp";
        stamp.textContent = "☠ Ship Has Sailed";
        card.appendChild(stamp);
      }
    }
  });

  // sailed shows go to the back (most recent first) so upcoming ones always lead
  [...document.querySelectorAll(".event-card--past")]
    .sort((a, b) => b.dataset.date.localeCompare(a.dataset.date))
    .forEach(card => card.parentElement.appendChild(card));
}

/* ---------- Generic language filter tabs ----------
   Wires up any [data-filter-tabs="lang"] group against the grid
   named in its data-filter-target, toggling .lot-card visibility by
   matching each card's data-lang attribute. Used by both the
   homepage Ledger and the Catalogue page. */
function wireFilterTabs() {
  document.querySelectorAll('[data-filter-tabs="lang"]').forEach(tabGroup => {
    const grid = document.getElementById(tabGroup.dataset.filterTarget);
    if (!grid) return;

    tabGroup.querySelectorAll(".ledger-tab").forEach(tab => {
      tab.addEventListener("click", () => {
        tabGroup.querySelectorAll(".ledger-tab").forEach(t => t.classList.remove("active"));
        tab.classList.add("active");
        const lang = tab.dataset.lang;
        grid.querySelectorAll(".lot-card").forEach(card => {
          card.style.display = (lang === "ALL" || card.dataset.lang === lang) ? "" : "none";
        });
      });
    });
  });
}

/* ---------- Homepage region-card shortcuts ----------
   Clicking English/Japanese/Chinese Waters scrolls to the Ledger
   and applies the matching language filter tab. */
function wireRegionCards() {
  document.querySelectorAll("[data-region]").forEach(card => {
    card.addEventListener("click", () => {
      const lang = card.dataset.region;
      const tab = document.querySelector(`#ledger [data-filter-tabs="lang"] [data-lang="${lang}"]`);
      tab?.click();
      document.getElementById("ledger")?.scrollIntoView({ behavior: "smooth" });
    });
  });
}

document.addEventListener("DOMContentLoaded", () => {
  wireChestDrawer();
  wireAddToCart();
  wireMobileNav();
  markPastShows();
  wireFilterTabs();
  wireRegionCards();
});
